import { createServerFn } from "@tanstack/react-start";
import { asc, eq } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug } from "../utils/slug.server";
import { invalidateInsightTagCache } from "./public.functions";

const statusSchema = z.enum(["draft", "published", "archived"]);

const tagInput = z.object({
  name: z.string().min(1),
  slug: z.string().optional(),
  status: statusSchema.default("published"),
  featured: z.boolean().default(false),
});

function countTagUsage(rows: { tags: string[] | null }[], name: string) {
  const needle = name.trim().toLowerCase();
  let count = 0;
  for (const row of rows) {
    for (const tag of row.tags ?? []) {
      if (tag.trim().toLowerCase() === needle) count++;
    }
  }
  return count;
}

async function nextTagSortOrder() {
  const rows = await getDb().select({ sortOrder: schema.insightTags.sortOrder }).from(schema.insightTags);
  return rows.length ? Math.max(...rows.map((r) => r.sortOrder)) + 1 : 0;
}

async function replaceTagOnInsights(oldName: string, newName: string) {
  const db = getDb();
  const rows = await db.select().from(schema.insights);
  for (const row of rows) {
    const tags = row.tags ?? [];
    const next = tags.map((tag) => (tag.trim().toLowerCase() === oldName.trim().toLowerCase() ? newName : tag));
    const changed = next.some((tag, i) => tag !== tags[i]);
    if (changed) {
      await db.update(schema.insights).set({ tags: next, updatedAt: new Date() }).where(eq(schema.insights.id, row.id));
    }
  }
}

async function removeTagFromInsights(name: string) {
  const db = getDb();
  const rows = await db.select().from(schema.insights);
  for (const row of rows) {
    const tags = row.tags ?? [];
    const next = tags.filter((tag) => tag.trim().toLowerCase() !== name.trim().toLowerCase());
    if (next.length !== tags.length) {
      await db.update(schema.insights).set({ tags: next, updatedAt: new Date() }).where(eq(schema.insights.id, row.id));
    }
  }
}

export const listInsightTagsAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("insights", "read");
  const db = getDb();
  const [tags, insightRows] = await Promise.all([
    db.select().from(schema.insightTags).orderBy(asc(schema.insightTags.sortOrder), asc(schema.insightTags.name)),
    db.select({ tags: schema.insights.tags }).from(schema.insights),
  ]);
  return tags.map((tag) => ({
    ...tag,
    usageCount: countTagUsage(insightRows, tag.name),
  }));
});

export const getInsightTagAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "read");
    const [row] = await getDb()
      .select()
      .from(schema.insightTags)
      .where(eq(schema.insightTags.id, data.id))
      .limit(1);
    return row ?? null;
  });

export const createInsightTag = createServerFn({ method: "POST" })
  .inputValidator(tagInput)
  .handler(async ({ data }) => {
    await requirePermission("insights", "write");
    const slug = data.slug || (await generateUniqueSlug(schema.insightTags, data.name));
    const [row] = await getDb()
      .insert(schema.insightTags)
      .values({
        name: data.name.trim(),
        slug,
        status: data.status,
        featured: data.featured,
        sortOrder: await nextTagSortOrder(),
      })
      .returning();
    await invalidateInsightTagCache();
    return row;
  });

export const updateInsightTag = createServerFn({ method: "POST" })
  .inputValidator(tagInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "write");
    const db = getDb();
    const [existing] = await db.select().from(schema.insightTags).where(eq(schema.insightTags.id, data.id)).limit(1);
    if (!existing) throw new Error("Not found");

    const slug = data.slug || existing.slug;
    const name = data.name.trim();
    const [row] = await db
      .update(schema.insightTags)
      .set({
        name,
        slug,
        status: data.status,
        featured: data.featured,
        updatedAt: new Date(),
      })
      .where(eq(schema.insightTags.id, data.id))
      .returning();

    if (existing.name.trim().toLowerCase() !== name.toLowerCase()) {
      await replaceTagOnInsights(existing.name, name);
    }
    await invalidateInsightTagCache();
    const { invalidateInsightCache } = await import("./public.functions");
    await invalidateInsightCache();
    return row;
  });

export const deleteInsightTag = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid(), removeFromArticles: z.boolean().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "delete");
    const db = getDb();
    const [existing] = await db.select().from(schema.insightTags).where(eq(schema.insightTags.id, data.id)).limit(1);
    if (!existing) throw new Error("Not found");

    const insightRows = await db.select({ tags: schema.insights.tags }).from(schema.insights);
    const usageCount = countTagUsage(insightRows, existing.name);
    if (usageCount > 0 && !data.removeFromArticles) {
      throw new Error(`This tag is used on ${usageCount} article(s). Remove it from articles first, or confirm removal.`);
    }
    if (usageCount > 0) {
      await removeTagFromInsights(existing.name);
    }

    await db.delete(schema.insightTags).where(eq(schema.insightTags.id, data.id));
    await invalidateInsightTagCache();
    const { invalidateInsightCache } = await import("./public.functions");
    await invalidateInsightCache();
    return { ok: true };
  });

export const reorderInsightTags = createServerFn({ method: "POST" })
  .inputValidator(z.object({ ids: z.array(z.string().uuid()).min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "write");
    const db = getDb();
    await Promise.all(
      data.ids.map((id, index) =>
        db.update(schema.insightTags).set({ sortOrder: index, updatedAt: new Date() }).where(eq(schema.insightTags.id, id)),
      ),
    );
    await invalidateInsightTagCache();
    return { ok: true };
  });
